import { useTranslation } from 'react-i18next';
import { CustomDatePicker } from '../';
import { Modal } from '../Modal';
import { useCallback, useState } from 'react';
import { Button } from '../Button';
import { Spinner } from '../Spinner';
import { FieldInfo } from '../FieldInfo';
import { useUpdateLastActiveHourlyRate } from '@/modules/Users/queries/update-last-active-hourly-rate';

type UpdateLasHourRateEndDateModalProps = {
  userId: number;
  isOpen: boolean;
  handleClose: () => void;
  handleAfterSubmit: () => void;
};

export const UpdateLasHourRateEndDateModal = ({
  userId,
  isOpen,
  handleClose,
  handleAfterSubmit,
}: UpdateLasHourRateEndDateModalProps) => {
  const { t } = useTranslation();
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(
    new Date(),
  );

  const { mutate, isLoading } = useUpdateLastActiveHourlyRate();

  const handleOnEndDateChange = useCallback(
    (date: Date | null) => setSelectedEndDate(date),
    [],
  );

  const handleEndDateSubmit = useCallback(() => {
    if (!selectedEndDate || !userId) {
      return;
    }

    mutate(
      { id: userId, endDate: selectedEndDate },
      { onSuccess: () => handleAfterSubmit() },
    );
  }, [handleAfterSubmit, mutate, selectedEndDate, userId]);

  return (
    <Modal
      isOpen={isOpen}
      title={t('update_date_last_hour_rate')}
      className="h-fit w-128 text-start"
      handleClose={handleClose}
    >
      <div
        className="h-70 mt-2 flex flex-col overflow-y-auto p-2"
        data-testid="update-last-hour-rate-section"
      >
        <FieldInfo title={t('rate_end_date')} hasError={!selectedEndDate}>
          <CustomDatePicker
            selected={selectedEndDate}
            onChange={handleOnEndDateChange}
            minDate={new Date()}
          />
        </FieldInfo>
      </div>
      <div className="mt-8 flex justify-between px-2">
        <Button onClick={handleClose} variant="outline" className="h-9 w-24">
          {t('cancel')}
        </Button>
        <Button
          onClick={handleEndDateSubmit}
          className="h-9 w-24"
          isDisabled={isLoading}
        >
          {t('confirm')}
          {isLoading && <Spinner className="ml-4 h-5 w-5" />}
        </Button>
      </div>
    </Modal>
  );
};
